import { useRef } from "react";

const PropertyCard = ({
  name,
  schemaTitle,
  property,
  side,
  onConnect,
  onRemoveConnection,
  isConnectable,
  connections,
  rightSchema,
  activeConnection,
  isRequired,
  isChecked,
  onCheckAttribute,
  comment,
  onCommentChange,
}) => {
  const getBadgeColor = (type) => {
    switch (type) {
      case "string":
        return "bg-info";
      case "integer":
        return "bg-success";
      case "number":
        return "bg-warning";
      case "boolean":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  const buttonRef = useRef(null);
  const fullId = `${schemaTitle}/${name}`;

  const isConnected =
    side === "left"
      ? connections?.some((conn) => conn.startName === fullId)
      : connections?.some((conn) => conn.endName === fullId);

  const linkedConnections =
    side === "left"
      ? connections?.filter((conn) => conn.startName === fullId)
      : [];

  // Fonction pour rendre les badges de type et autres informations
  const renderPropertyBadges = (prop) => (
    <div className="badges ">
      <span className={`badge text-white ${getBadgeColor(prop.type)} mr-1`}>
        {prop.type}
      </span>
      {prop.maxLength && (
        <span className="badge text-white bg-secondary">
          max: {prop.maxLength}
        </span>
      )}
      {prop.nullable && (
        <span className="badge text-white bg-warning ml-1">nullable</span>
      )}
      {prop.$ref && (
        <span className="badge text-white bg-danger ml-1">
          référence interne
        </span>
      )}
    </div>
  );

  // Fonction pour rendre les détails d'une propriété
  const renderPropertyDetails = (prop, isMainProperty = false) => (
    <div className="px-3">
      {prop.description && (
        <p className="mb-0 text-muted small">{prop.description}</p>
      )}

      <div className="mt-0 mb-2">
        {prop.format && (
          <div className="text-muted small">
            <strong>Format:</strong> {prop.format}
          </div>
        )}
        {prop.format === "select" && prop.values && (
          <div className="text-muted small mt-2">
            <strong>Valeurs possibles:</strong>
            <ul className="list-unstyled ms-3 mb-0">
              {prop.values.map((val, index) => (
                <li key={index}>
                  <code>{val.value}</code> - {val.description}
                </li>
              ))}
            </ul>
          </div>
        )}
        {prop.minLength && (
          <div className="text-muted small">
            <strong>Min Length:</strong> {prop.minLength}
          </div>
        )}
        {prop.$ref && (
          <div className="text-muted small">
            <strong>Reference:</strong> {prop.$ref}
          </div>
        )}
        {renderPropertyBadges(prop)}
        {isMainProperty && side === "left" && isChecked && (
          <div className="my-2">
            <input
              type="text"
              className="form-control form-control-sm p-1"
              placeholder="Ajouter un commentaire..."
              value={comment || ""}
              onChange={(e) => onCommentChange(e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );

  // Détermine si le bouton doit être affiché
  const showButton =
    (side === "left" &&
      (!activeConnection || activeConnection.start === fullId)) ||
    (side === "right" && activeConnection);

  return (
    <div className="position-relative bg-white">
      <div className={`property-card mb-2 ${isConnected ? "connected" : ""}`}>
        <div className="mb-1 ">
          <div className="d-flex">
            {/* Colonne de gauche - Propriété initiale */}
            <div className="flex-grow-1 border-end ">
              <div className="pt-2 pb-1 px-3 ">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    {side === "left" && (
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="me-2"
                          checked={isChecked}
                          onChange={(e) => onCheckAttribute(e.target.checked)}
                        />
                      </div>
                    )}

                    <span
                      className={`font-weight-bold ml-1 ${
                        isRequired ? "text-danger" : ""
                      }`}
                    >
                      {name}
                    </span>
                    {isConnectable && showButton && (
                      <button
                        ref={buttonRef}
                        className={`btn py-0 d-center px-1 mx-1 ${
                          isConnected
                            ? "btn-success"
                            : side === "left"
                            ? "btn-primary"
                            : "btn-secondary"
                        }`}
                        style={{
                          //   position: "absolute",
                          //   top: 35,
                          //   right: -20,
                          //   zIndex: "999",
                          height: 16,
                          fontSize: 11,
                          padding: 0,
                          lineHeight: "0px",
                        }}
                        onClick={() =>
                          onConnect(fullId, buttonRef.current, side)
                        }
                        disabled={side === "right" && !activeConnection}
                      >
                        {isConnected ? "Lié" : "Lier"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {renderPropertyDetails(property, true)}
              {/* {renderPropertyBadges(property)} */}
            </div>

            {/* Colonne de droite - Propriétés liées */}
            {side === "left" && linkedConnections.length > 0 && (
              <div className="flex-grow-1" style={{ maxWidth: "50%" }}>
                <div className="linked-properties border-left">
                  {linkedConnections.map((conn, index) => {
                    const [schema, prop] = conn.endName.split("/");

                    const linkedProperty = rightSchema[schema].properties[prop];
                    const isLinkedRequired =
                      rightSchema[schema].required?.includes(prop);
                    if (!linkedProperty) return null;

                    return (
                      <div key={index} className="position-relative">
                        <div className="py-2 px-3 bg-white">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <button
                                className="btn btn-sm btn-outline-danger mr-1"
                                onClick={() => onRemoveConnection(conn)}
                              >
                                ×
                              </button>
                              <span
                                className={`font-weight-bold ${
                                  isLinkedRequired ? "text-danger" : ""
                                }`}
                              >
                                {schema}/{prop}
                              </span>
                            </div>
                            {/* {renderPropertyBadges(linkedProperty)} */}
                          </div>
                        </div>
                        {renderPropertyDetails(linkedProperty, false)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default PropertyCard;
